/**
 * Preview button functionality for recipes#show;
 * handles toggling between normal and print-like views.
 */
import { idleCallback } from "~/label/util";

const DESKTOP_BREAKPOINT = 1023;

const SELECTORS = {
  sidebarColumn: "#sidebar-options-column",
  selectFormatContainer: "#labeltype-container",
  labelAndClaimDiv: "#label-and-claim-div",
  previewButton: "#preview-toggle",
};

const ELEMENTS_TO_HIDE = [
  "#recipe-option-toggles",
  "#nutrition-claims",
  "#canada-fop-symbols",
  "#label-actions-main",
  "#label-actions-alt",
  "#bottom-buttons",
  ".tutorial-button-wrapper",
  ".supplement-editor-sidebar",
  ".supplement-label-name",
  ".no-print",
];

const toggleHiddenElements = (isHidden) => {
  ELEMENTS_TO_HIDE.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el) => {
      // Don't hide the preview button itself or any ancestor that contains it
      const previewButton = document.querySelector("#preview-toggle");
      if (
        el.matches("#preview-toggle") ||
        el.closest("#preview-toggle") ||
        (previewButton && el.contains(previewButton))
      ) {
        return;
      }

      if (selector !== "#recipe-option-toggles" || el.closest("#sidebar-options-column")) {
        el.style.visibility = isHidden ? "hidden" : "";
      }
    });
  });
};

const setPreviewState = (isPreview, elements) => {
  const { sidebarColumn, selectFormatContainer, labelAndClaimDiv, previewButton } = elements;

  if (!previewButton) return;

  const buttonText = previewButton.querySelector("span:not(.icon)");
  buttonText.textContent = isPreview ? "Exit Preview" : "Preview";
  previewButton.querySelector(".icon").classList.toggle("is-hidden", isPreview);
  previewButton.classList.toggle("is-active", isPreview);
  previewButton.classList.toggle("is-primary", isPreview);
  previewButton.classList.toggle("is-info", !isPreview);
  previewButton.classList.toggle("is-outlined", !isPreview);

  if (labelAndClaimDiv) {
    labelAndClaimDiv.classList.toggle("printlike", isPreview);
  }

  if (sidebarColumn) {
    if (isPreview) {
      sidebarColumn.style.width = `${sidebarColumn.offsetWidth}px`;
    } else {
      sidebarColumn.style.removeProperty("width");
    }
  }

  toggleHiddenElements(isPreview);

  if (selectFormatContainer) {
    selectFormatContainer.style.display = isPreview ? "none" : "";
  }

  if (!isPreview) {
    previewButton.blur();
  }
};

const getElements = () =>
  Object.entries(SELECTORS).reduce((acc, [key, selector]) => {
    const element = document.querySelector(selector);
    // selectFormatContainer and previewButton are optional (not present on supplement labels page)
    if (!element && key !== "selectFormatContainer" && key !== "previewButton") {
      console.error(`Code drift: element not found for selector "${selector}"`);
    }
    acc[key] = element;
    return acc;
  }, {});

let cleanupPreviewEvents = null;

const setup = () => {
  if (cleanupPreviewEvents) {
    cleanupPreviewEvents();
    cleanupPreviewEvents = null;
  }

  const elements = getElements();
  const { previewButton } = elements;
  if (!previewButton) return;

  const handleClickPreviewButton = () => {
    const isPreview = !previewButton.classList.contains("is-active");
    setPreviewState(isPreview, elements);
  };

  const handleWindowResize = () => {
    if (window.innerWidth <= DESKTOP_BREAKPOINT && previewButton.classList.contains("is-active")) {
      setPreviewState(false, elements);
    }
  };

  previewButton.addEventListener("click", handleClickPreviewButton);
  window.addEventListener("resize", handleWindowResize);

  cleanupPreviewEvents = () => {
    previewButton.removeEventListener("click", handleClickPreviewButton);
    window.removeEventListener("resize", handleWindowResize);
  };
};

export const bindPreviewButton = () => idleCallback(setup);
